import PropTypes from 'prop-types';
import {
  Box, Grid, Text, Group, Tooltip,
} from '@mantine/core';
import { IconFileTextSpark, IconFileTextShield, IconInfoCircle } from '@tabler/icons-react';
import NumberFlowDisplay from '@/components/NumberFlowDisplay';
import classes from './riskComparisonTable.module.css';
import CurrencyNisIcon from '@/assets/images/currencyNis.svg?react'; // eslint-disable-line import/no-unresolved

const format = (value, shouldRound = true) => {
  if (value === undefined) return '---';
  if (typeof value === 'number') {
    return shouldRound
      ? Math.round(value).toLocaleString()
      : value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
  }
  return value;
};

const formatYearRange = (start, end) => (start !== undefined && end !== undefined ? `${start} עד ${end} שנים` : '---');

const TableRow = ({ index, children }) => (
  <Grid
    grow
    gutter="xs"
    align="center"
    className={`${classes.tableRow} ${index % 2 === 0 ? classes.tableRowAlt : ''}`}

  >
    {children}
  </Grid>
);

TableRow.propTypes = {
  index: PropTypes.number.isRequired,
  children: PropTypes.node,
};

const RiskComparisonTable = ({ data, period, mortgagePaymentCapacity }) => {
  const risks = data.map((d) => d.risk);
  const suffix = <CurrencyNisIcon style={{ width: '12px' }} />;
  if (risks.length === 0) return null;

  const getMix = (risk) => {
    const group = data.find((d) => d.risk === risk);
    return group?.mixes.find((mix) => mix.period === period);
  };

  const getPeriodRange = (risk) => {
    const group = data.find((d) => d.risk === risk);
    return {
      min: group?.minPeriod,
      max: group?.maxPeriod,
    };
  };

  if (risks.length === 1 && !getMix(risks[0])) return null;

  const sortedRisks = [...risks].sort((a, b) => Number(a) - Number(b));

  return (
    <Box p="md" radius="md">
      <Grid grow mb="sm">
        {risks.length > 1 && <Grid.Col span={12 / (risks.length + 1)} />}
        {sortedRisks.map((risk, index) => {
          const mix = getMix(risk);
          const isRecommended = index === 0;

          return (
            <Grid.Col
              key={risk}
              span={
                12 / (
                  sortedRisks.length
                  + (sortedRisks.length > 1 ? 1 : 0)
                )
              }
            >
              <Box p="xs" radius="md" className={classes.box}>
                <Box>
                  <Box justify="center" align="center" gap="xs" className={classes.headerBox}>
                    <Box className={classes.iconBox}>
                      {isRecommended ? <IconFileTextSpark size={22} color="#5f3dc4" /> : <IconFileTextShield size={22} color="#5f3dc4" />}
                    </Box>
                    <Text size="sm" className={classes.boxTitle} c="#5f3dc4" fw={500}>
                      {isRecommended ? 'מסלול מומלץ' : 'מסלול בסיכון גבוה יותר'}
                    </Text>
                  </Box>
                </Box>

                {mix?.bankTotalReturn && mix?.totalReturn ? (
                  <Box className={classes.priceTextWrapper}>
                    <p className={classes.priceText}>
                      <NumberFlowDisplay
                        value={Math.round(Number(mix.bankTotalReturn) - Number(mix.totalReturn))}
                        suffix={suffix} />
                    </p>
                    <p className={classes.priceTextSmall}>
                      הסכום שנוכל לחסוך עבורכם
                    </p>
                  </Box>
                ) : (
                  <Box className={classes.priceTextWrapper}>
                    <p className={classes.priceText}>
                      לא זמין
                    </p>
                    <p>
                      תמהיל לא זמין למספר השנים
                    </p>
                  </Box>
                )}

              </Box>
            </Grid.Col>
          );
        })}
      </Grid>

      {/* Data Rows */}
      {[
        {
          label: 'מספר שנים נבחר',
          getValue: (mix) => format(mix?.period),
        },
        {
          label: 'החזר חודשי נבחר',
          getValue: (mix) => (mix?.utilizedRepaymentCapacity !== undefined
            ? mix.utilizedRepaymentCapacity
            : '---'),
          suffix,
          round: true,
        },
        {
          label: 'יחס החזר',
          getValue: (mix) => format(mix?.returnConversion, false),
          round: false,
        },
        {
          label: (
            <Group gap={4} ta="center" justify="center">
              <Text>טווח שנים אפשרי</Text>
              <Tooltip label="תקופת החזר מקסימלית בהתאם לגיל הלווים">
                <IconInfoCircle color="#5f3dc4" size={15} />
              </Tooltip>
            </Group>
          ),
          getValue: (_, risk) => {
            const { min, max } = getPeriodRange(risk);
            return formatYearRange(min, max);
          },
          suffix,
        },
        {
          label: (
            <Group gap={4} ta="center" justify="center">
              <Text>סכום החזר מקסימלי על פי להכנסתך</Text>
              <Tooltip label="זהו הסכום המקסימלי שהוגדר עבורך להחזר חודשי לפי הנתונים שסיפקת">
                <IconInfoCircle color="#5f3dc4" size={15} />
              </Tooltip>
            </Group>
          ),
          getValue: (mix) => (mix?.utilizedRepaymentCapacity
            ? mortgagePaymentCapacity
            : '---'),
          suffix,
          round: true,
        },
        {
          label: 'סכום החזר מינימלי',
          getValue: (_, risk) => {
            const group = data.find((d) => d.risk === risk);
            return group?.minUtilizedRepaymentCapacity
              ? group.minUtilizedRepaymentCapacity
              : '---';
          },
          suffix,
          round: true,
        },
        {
          label: 'סה"כ החזר בסוף התקופה',
          getValue: (mix) => (mix?.totalReturn
            ? mix.totalReturn
            : '---'),
          suffix,
          round: true,
        },
      ].map((row, i) => (
        <TableRow key={i} index={i}>
          <Grid.Col span={12 / (risks.length + 1)} ta="center">
            <Text fw={600}>{row.label}</Text>
          </Grid.Col>
          {risks.map((risk) => {
            const mix = getMix(risk);
            return (
              <Grid.Col key={risk} span={12 / (risks.length + (risks.length > 1 ? 1 : 0))} ta="center">
                <Text className={classes.tableCellText}>
                  {Number(row.getValue(mix, risk)) ? (
                    <NumberFlowDisplay
                      value={
                        row.round
                          ? Math.round(row.getValue(mix, risk))
                          : row.getValue(mix, risk)
                      }
                      suffix={row.suffix}
                    />
                  ) : (
                    row.getValue(mix, risk)
                  )}
                </Text>
              </Grid.Col>
            );
          })}
        </TableRow>
      ))}
    </Box>
  );
};

RiskComparisonTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      risk: PropTypes.string,
      minPeriod: PropTypes.number,
      maxPeriod: PropTypes.number,
      mixes: PropTypes.arrayOf(
        PropTypes.shape({
          period: PropTypes.number,
          risk: PropTypes.number,
          creditPro: PropTypes.bool,
          utilizedRepaymentCapacity: PropTypes.number,
          totalReturn: PropTypes.number,
          returnConversion: PropTypes.number,
          bankTotalReturn: PropTypes.number,
        }),
      ),
    }),
  ).isRequired,
  period: PropTypes.number.isRequired,
  mortgagePaymentCapacity: PropTypes.number.isRequired,
};

export default RiskComparisonTable;
